import { AlertColor } from "@mui/material";
import { CardElement } from "@stripe/react-stripe-js";
import { Stripe, StripeCardElement, StripeElements } from "@stripe/stripe-js";
import { Auth } from "aws-amplify";
import { User } from "contexts/identity-context";
import { OrganisationRow } from "contexts/models";
import {
  AdminAccountDetails,
  AdminOrganisationInfo,
  InvitationAlertState,
  ApigeeDeveloperStatus,
  BuildingsSampleRequest,
  Connector,
  Coupon,
  CreateRelease,
  CreditsInfo,
  CustomDataset,
  CustomDatasetEdit,
  DatasetStatus,
  DemoStatus,
  DeveloperStatus,
  InvitationResponse,
  ManualTriggerResponse,
  MigrateMembersRequest,
  MigrateMembersResponse,
  ModifyApiKey,
  OrgMembership,
  Organisation,
  PaymentMethodAdmin,
  Pipeline,
  PipelineStatusBody,
  RejectedInvite,
  Release,
  ReleaseStatus,
  UpdateSubscription,
  UserMembership,
} from "data/models";
import { CustomDatasetRowType } from "pages/geoscape-admin/custom-datasets/custom-hook/customDatasetRows";

import { InvitationItem } from "components/settings/organization/members/invite-members";
import {
  AdminOrganisationDetail,
  AdminOrganisationVault,
} from "pages/geoscape-admin/organisation/organisation-details/utils";
import { SubmitEditAdminUserSubscription } from "pages/geoscape-admin/organisation/organisation-subscription/types";
import { BatchConfig } from "pages/geoscape-batch/models";
import {
  ClipConfiguration,
  GeoJSONFeatureCollection,
} from "pages/geoscape-data/clip/clip-ui/models";
import { makeApiRequestPostToUrl, makeApiRequestToUrl } from "utils/api-client";
import { PlanDetails, roundDownOverageBudget } from "utils/calculations";
import { CustomDatasetsPost, PaymentMethod } from "./models";
import { DefaultError } from "@tanstack/react-query";

type ReactQueryMutationError = DefaultError;

const getStripePaymentMethodToken = async (
  stripe: Stripe | null,
  stripeElements: StripeElements | null,
) => {
  if (!stripe || !stripeElements) {
    throw new Error("Unable to access stripe. Please contact support.");
  }

  // Use elements.getElement to get a reference to the mounted Element.
  const cardElement: StripeCardElement | null =
    stripeElements.getElement(CardElement);
  if (!cardElement) {
    throw new Error("Unable to access stripe. Please contact support.");
  }

  // Pass the Element directly to other Stripe.js methods:
  const tokenResult = await stripe.createToken(cardElement);

  if (tokenResult.error) {
    throw new Error(tokenResult.error.message);
  }

  return tokenResult;
};

const adminAddPaymentMethod = async (
  stripe: Stripe | null,
  stripeElements: StripeElements | null,
  organisationId: string,
  setEditingCC: any,
): Promise<PaymentMethodAdmin> => {
  const tokenResult = await getStripePaymentMethodToken(stripe, stripeElements);
  let paymentDetails: PaymentMethodAdmin[];

  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/${organisationId}/paymentMethod`,
      "POST",
      tokenResult.token,
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages[0]);
    }

    setEditingCC(false);

    paymentDetails = responseData.filter(
      (paymentMethod: PaymentMethodAdmin) => paymentMethod.active,
    );
  } catch (err) {
    throw new Error(
      `Unable to add payment method as admin for organisation '${organisationId}' : ${err}`,
    );
  }

  return paymentDetails[0];
};

const adminUpdateCredits = async (
  organisationId: string,
  subCreditsLimit: number,
  overageDollarsLimit: number,
): Promise<CreditsInfo> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/credits/${organisationId}`,
      "PUT",
      {
        subCreditsLimit,
        overageDollarsLimit,
      },
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages[0]);
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to update credits info as admin for organisation '${organisationId}' : ${err}`,
    );
  }
};

const addPaymentMethod = async (
  stripe: Stripe | null,
  stripeElements: StripeElements | null,
  setEditingCC: any,
): Promise<PaymentMethod> => {
  const tokenResult = await getStripePaymentMethodToken(stripe, stripeElements);
  let paymentDetails: PaymentMethod[];
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/paymentMethod",
      "POST",
      tokenResult.token,
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages[0]);
    }

    setEditingCC(false);

    paymentDetails = responseData.filter(
      (paymentMethod: PaymentMethod) => paymentMethod.active,
    );
  } catch (err) {
    throw new Error(`Unable to add payment method: ${err}`);
  }

  return paymentDetails[0];
};

async function updateOverageLimit(
  newDollarLimit: number,
  planDetails: PlanDetails,
): Promise<CreditsInfo> {
  const newOverageLimit: number = roundDownOverageBudget(
    newDollarLimit,
    planDetails,
  );

  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/overage",
      "POST",
      { limitDollars: newOverageLimit },
    );
    const newOverage = await response.json();

    if (response.status !== 200) {
      throw new Error(newOverage.messages.join(". "));
    }

    return newOverage;
  } catch (err) {
    throw new Error(
      `Unable to update overage limit to $${newDollarLimit} : ${err}`,
    );
  }
}

const submitNewOrganisation = async (orgDetails: Organisation) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/v2/organisation",
      "POST",
      orgDetails,
    );
    const newOrg = await response.json();

    if (response.status !== 200) {
      throw new Error(newOrg.messages.join(". "));
    }

    return newOrg;
  } catch (err) {
    throw new Error(
      `Unable to submit new organisation '${orgDetails.name}' : ${err}`,
    );
  }
};

const editOrganisation = async (values: Organisation) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/v2/organisation",
      "PUT",
      values,
    );
    const updatedOrganisation = await response.json();

    if (response.status !== 200) {
      throw new Error(updatedOrganisation.messages.join(". "));
    }

    return updatedOrganisation;
  } catch (err) {
    throw new Error(
      `Unable to update organisation details for '${values.name}' : ${err}`,
    );
  }
};

const putRelease = async (
  values: CreateRelease,
  status: ReleaseStatus,
): Promise<Release> => {
  let values_ = { ...values, status: status };

  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/admin/datasets/release",
      "POST",
      values_,
    );
    const newRelease = await response.json();

    if (response.status !== 200) {
      throw new Error(newRelease.messages.join(". "));
    }

    return newRelease;
  } catch (err) {
    throw new Error(`Unable to stage a new data release: ${err}`);
  }
};

const changeReleaseStatus = async (
  release_id: string,
  status: ReleaseStatus,
): Promise<Release> => {
  let value = { status: status };

  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/release/${release_id}`,
      "PUT",
      value,
    );
    const newRelease = await response.json();

    if (response.status !== 200) {
      throw new Error(newRelease.messages.join(". "));
    }

    return newRelease;
  } catch (err) {
    throw new Error(
      `Unable to change release '${release_id}' status to '${status}' : ${err}`,
    );
  }
};

const deleteRelease = async (releaseId: string): Promise<void> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/release/${releaseId}`,
      "DELETE",
      {},
    );

    if (response.status !== 200) {
      const error = await response.json();
      throw new Error(error.messages?.join(". ") || "Failed to delete release");
    }
  } catch (err) {
    throw new Error(`Unable to delete release '${releaseId}': ${err}`);
  }
};

const changeDatasetStatus = async (ids: string[], status: DatasetStatus) => {
  let value = {
    ids: ids,
    status: status,
  };
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/manage`,
      "POST",
      value,
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(
      `Unable to change '${ids.length}' dataset statuses to '${status}' : ${err}`,
    );
  }
};

const deleteOrganisationInvitation = async (
  organisationApigeeDeveloperId: string,
  developerApigeeDeveloperId: string,
) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/organisation/membership?orgId=${organisationApigeeDeveloperId}&devId=${encodeURIComponent(
        developerApigeeDeveloperId,
      )}`,
      "DELETE",
    );
    const deletedOrganisationInvitation = await response.json();

    if (response.status !== 200) {
      throw new Error(deletedOrganisationInvitation.messages.join(". "));
    }

    return deletedOrganisationInvitation;
  } catch (err) {
    throw new Error(
      `Unable to delete organisation invitation for developer '${developerApigeeDeveloperId}' in organisation '${organisationApigeeDeveloperId}' : ${err}`,
    );
  }
};

const deleteOrganisationMember = async (
  organisationId: string,
  userMemberId: string,
) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/organisation/membership?organisationId=${organisationId}&userMemberId=${encodeURIComponent(
        userMemberId,
      )}`,
      "DELETE",
    );
    const deletedOrganisationMember = await response.json();

    if (response.status !== 200) {
      throw new Error(deletedOrganisationMember.messages.join(". "));
    }

    return deletedOrganisationMember;
  } catch (err) {
    throw new Error(
      `Unable to delete organisation member '${userMemberId}' from organisation '${organisationId}' : ${err}`,
    );
  }
};

const submitOrganisationInvitations = async (
  invitations: InvitationItem[],
  organisationId: string,
): Promise<InvitationAlertState[]> => {
  try {
    const responses = await Promise.all(
      invitations.map((invitation) =>
        makeApiRequestPostToUrl(
          `${import.meta.env.VITE_DELIVERY_API_URL}`,
          "/v2/organisation/membership",
          "POST",
          {
            userEmail: invitation.email,
            organisationId: organisationId,
            invitationStatus: "PENDING",
            role: invitation.role,
          },
        ),
      ),
    );

    const alertStateList: InvitationAlertState[] = [];
    for (let i = 0; i < responses.length; i++) {
      const r = responses[i];
      const res = await r.json();
      const invitation = invitations[i];

      let message = res?.messages?.join(". ");
      let severity: AlertColor = "info";
      if (r.status !== 200) {
        severity = "error";
      } else {
        message = res.userExisted
          ? `Invitation to ${invitation.email} has been sent. Invited user is already registered with Geoscape.`
          : `Invitation to ${invitation.email} has been sent. Invited user does not exist in Geoscape. They may need to sign up.`;
        severity = "info";
      }
      alertStateList.push({
        message: message,
        severity: severity,
      });
    }

    return alertStateList;
  } catch (err) {
    throw new Error(
      `Unable to submit organisation invitations for organisation '${organisationId}': ${err}`,
    );
  }
};

const editApiKey = async (updatedValues: ModifyApiKey) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/apiKeys/${encodeURIComponent(updatedValues.name)}`,
      "PUT",
      updatedValues,
    );
    const updatedApiKey = await response.json();

    if (response.status !== 200) {
      throw new Error(updatedApiKey.messages.join(". "));
    }

    return updatedApiKey;
  } catch (err) {
    throw new Error(
      `Unable to update api key details for api key '${updatedValues.displayName}' : ${err}`,
    );
  }
};

const deleteApiKey = async (apiKeyName: string) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/apiKeys/${encodeURIComponent(apiKeyName)}`,
      "DELETE",
    );
    const deletedApiKey = await response.json();

    if (response.status !== 200) {
      throw new Error(deletedApiKey.messages.join(". "));
    }

    return deletedApiKey;
  } catch (err) {
    throw new Error(`Unable to delete api key '${apiKeyName}' : ${err}`);
  }
};

const deleteBatchJobs = async (jobs: string[]) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/delete`,
      "POST",
      jobs,
    );
    const deletedJobs = await response.json();

    if (response.status !== 200) {
      throw new Error(deletedJobs.messages.join(". "));
    }

    return deletedJobs;
  } catch (err) {
    throw new Error(`Unable to delete '${jobs.length}' batch jobs: ${err}`);
  }
};

const deleteClip = async (clipId: string) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/config/${clipId}`,
      "DELETE",
    );

    if (response.status !== 200) {
      const responseData = await response.json();
      throw new Error(
        responseData.messages?.join(". ") || "Failed to delete clip",
      );
    }

    return response;
  } catch (err) {
    throw new Error(`Unable to delete clip: ${err}`);
  }
};

const createApiKey = async (apiKeyValues: ModifyApiKey) => {
  try {
    apiKeyValues.displayName = apiKeyValues.name;

    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/apiKeys`,
      "POST",
      apiKeyValues,
    );
    const createdApiKey = await response.json();

    if (response.status !== 200) {
      throw new Error(createdApiKey.messages.join(". "));
    }

    createdApiKey.status = "approved";

    return createdApiKey;
  } catch (err) {
    throw new Error(
      `Unable to create api key '${apiKeyValues.displayName}': ${err}`,
    );
  }
};

const updateSubscription = async (values: UpdateSubscription) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/subscriptions",
      "POST",
      values,
    );
    const updatedSubscription = await response.json();

    if (response.status !== 200) {
      throw new Error(updatedSubscription.messages.join(". "));
    }

    return updatedSubscription;
  } catch (err) {
    throw new Error(`Unable to update subscription: ${err}`);
  }
};

const cancelFutureSubscription = async () => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/nextSubscription",
      "DELETE",
    );
    const deletedFutureSubscription = await response.json();

    if (response.status !== 200) {
      throw new Error(deletedFutureSubscription.messages.join(". "));
    }

    return deletedFutureSubscription;
  } catch (err) {
    throw new Error(`Unable to delete subscription: ${err}`);
  }
};

const undoScheduledCancellation = async () => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/cancelSubscription",
      "DELETE",
    );
    const cancellation = await response.json();

    if (response.status !== 200) {
      throw new Error(cancellation.messages.join(". "));
    }

    return cancellation;
  } catch (err) {
    throw new Error(`Unable to undo cancellation: ${err}`);
  }
};

const scheduleCancellation = async () => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/subscription",
      "DELETE",
    );
    const cancellation = await response.json();

    if (response.status !== 200) {
      throw new Error(cancellation.messages.join(". "));
    }

    return cancellation;
  } catch (err) {
    throw new Error(`Unable to cancel subscription: ${err}`);
  }
};

const adminUndoScheduledCancellation = async (organisationId: string) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/subscriptions/cancelSubscription/${organisationId}`,
      "DELETE",
    );
    const cancellation = await response.json();

    if (response.status !== 200) {
      throw new Error(cancellation.messages.join(". "));
    }

    return cancellation;
  } catch (err) {
    throw new Error(`Unable to undo cancellation: ${err}`);
  }
};

const adminDeleteOrganisationMember = async (
  organisationId: string,
  userId: string,
) => {
  try {
    const encodedOrgId = encodeURIComponent(organisationId);
    const encodedDevId = encodeURIComponent(userId);
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/members?organisationId=${encodedOrgId}&userId=${encodedDevId}`,
      "DELETE",
    );
    const deletedOrganisationMember = await response.json();

    if (response.status !== 200) {
      throw new Error(deletedOrganisationMember.messages.join(". "));
    }

    return deletedOrganisationMember;
  } catch (err) {
    throw new Error(
      `Unable to delete member '${userId}' from the organisation '${organisationId}' : ${err}`,
    );
  }
};

async function updateAdminUserRoleOrgMembership(
  role: string,
  accountId: string,
  orgId: string,
): Promise<OrgMembership> {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/${orgId}/member/${accountId}/role`,
      "PUT",
      { role },
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to update user role as admin for '${accountId}' to '${role}' : ${err}`,
    );
  }
}

async function updateUserRoleOrgMembership(
  role: string,
  accountId: string,
  orgId: string,
): Promise<OrgMembership> {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/organisation/${orgId}/member/${accountId}/role`,
      "PUT",
      { role },
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to update user role for '${accountId}' to '${role}' : ${err}`,
    );
  }
}

const userRejectInvitation = async (
  organisationId: string,
): Promise<RejectedInvite> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/user/membership/${organisationId}`,
      "DELETE",
      {},
    );
    const newMembership = await response.json();

    if (response.status !== 200) {
      throw new Error(newMembership.messages.join(". "));
    }

    return newMembership;
  } catch (err) {
    throw new Error(
      `Failed to accept invitation for org ${organisationId} - ${err}`,
    );
  }
};

const userAcceptInvitation = async (
  organisationId: string,
): Promise<UserMembership> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/v2/user/membership",
      "POST",
      {
        organisationId: organisationId,
      },
    );
    const newMembership = await response.json();

    if (response.status !== 200) {
      throw new Error(newMembership.messages.join(". "));
    }

    return newMembership;
  } catch (err) {
    throw new Error(
      `Failed to accept invitation for org ${organisationId} - ${err}`,
    );
  }
};

const adminAddOrganisationMember = async (
  organisationId: string,
  invitations: InvitationItem[],
): Promise<InvitationResponse[]> => {
  try {
    // Calls the api X times to invite X emails. Unlikely optimal, but makes it very simple
    const responses = await Promise.all(
      invitations.map((invitation) =>
        makeApiRequestPostToUrl(
          `${import.meta.env.VITE_DELIVERY_API_URL}`,
          "/admin/organisation/members",
          "POST",
          {
            organisationId: organisationId,
            userEmail: invitation.email,
            invitationStatus: "PENDING",
            role: invitation.role,
          },
        ),
      ),
    ).then((results) => Promise.all(results.map((result) => result.json())));
    return responses;
  } catch (err) {
    throw new Error(
      `Unable to add '${invitations.length}' emails to the organisation as admin: ${err}`,
    );
  }
};

const adminSetOrganisationStatus = async (
  organisationId: string,
  apigeeDeveloperStatus: ApigeeDeveloperStatus,
): Promise<DeveloperStatus> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/status/${organisationId}`,
      "PUT",
      {
        action: apigeeDeveloperStatus.action,
      },
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }
    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to change the developer's status as admin for account '${apigeeDeveloperStatus.apigeeDeveloperId}' to '${apigeeDeveloperStatus.action}' : ${err}`,
    );
  }
};

const editAdminOrganisation = async (
  organisationId: string,
  values: AdminOrganisationDetail,
): Promise<any> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/${organisationId}`,
      "PUT",
      values,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to edit the organisation's details as admin for organisation '${organisationId}' : ${err}`,
    );
  }
};

const editAdminOrganisationInfo = async (
  organisationId: string,
  values: AdminOrganisationInfo,
): Promise<AdminOrganisationInfo> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/${organisationId}/info`,
      "PUT",
      values,
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to edit the organisation's info as admin for organisation '${organisationId}' : ${err}`,
    );
  }
};

const createAdminOrganisation = async (
  values: Organisation,
): Promise<OrganisationRow> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation`,
      "POST",
      values,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to create the organisation as admin for organisation: ${err}`,
    );
  }
};

const editAdminAccount = async (
  accountId: string,
  values: AdminAccountDetails,
): Promise<any> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/accounts/${accountId}`,
      "PUT",
      values,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to edit the account details as admin for account '${accountId}' : ${err}`,
    );
  }
};

const deleteAdminUser = async (organisationId: string): Promise<any> => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/${organisationId}`,
      "DELETE",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to delete customer as admin for organisation '${organisationId}' : ${err}`,
    );
  }
};

const adminDeleteAccount = async (accountId: string): Promise<any> => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/accounts/${accountId}`,
      "DELETE",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to delete account as admin for account:'${accountId}' : ${err}`,
    );
  }
};

const adminDisableAccount = async (accountId: string): Promise<any> => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/accounts/${accountId}/disable`,
      "PUT",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to disable account as admin for account:'${accountId}' : ${err}`,
    );
  }
};

const adminEnableAccount = async (accountId: string): Promise<any> => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/accounts/${accountId}/enable`,
      "PUT",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to enable account as admin for account:'${accountId}' : ${err}`,
    );
  }
};

const deleteAdminUserMembership = async (
  organisationId: string,
  userMemberId: string,
) => {
  try {
    const encodedOrgId = encodeURIComponent(organisationId);
    const encodedDevId = encodeURIComponent(userMemberId);
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/user/membership?organisationId=${encodedOrgId}&userMemberId=${encodedDevId}`,
      "DELETE",
    );
    const deletedOrganisationMember = await response.json();

    if (response.status !== 200) {
      throw new Error(deletedOrganisationMember.messages.join(". "));
    }

    return deletedOrganisationMember;
  } catch (err) {
    throw new Error(
      `Unable to delete member '${userMemberId}' from the organisation '${organisationId}' : ${err}`,
    );
  }
};

const editAdminUserSubscription = async (
  organisationId: string,
  values: SubmitEditAdminUserSubscription,
) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/subscriptions/${organisationId}`,
      "PUT",
      values,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to edit subscription for user as admin for organisation '${organisationId}' : ${err}`,
    );
  }
};

const cancelAdminUserSubscription = async (
  organisationId: string,
  values: SubmitEditAdminUserSubscription,
) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/subscriptions/${organisationId}`,
      "DELETE",
      values,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to edit subscription for user as admin for organisation '${organisationId}' : ${err}`,
    );
  }
};

const createAdminUserApiKey = async (
  apiKeyValues: ModifyApiKey,
  organisationId: string,
) => {
  try {
    apiKeyValues.displayName = apiKeyValues.name;

    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/apiKeys?organisationId=${organisationId}`,
      "POST",
      apiKeyValues,
    );
    const createdApiKey = await response.json();

    if (response.status !== 200) {
      throw new Error(createdApiKey.messages.join(". "));
    }

    createdApiKey.status = "approved";

    return createdApiKey;
  } catch (err) {
    throw new Error(
      `Unable to create api key for user as admin for organisation '${organisationId}' and api key '${apiKeyValues.displayName}' : ${err}`,
    );
  }
};

const editAdminUserApiKey = async (
  apiKeyValues: ModifyApiKey,
  organisationId: string,
) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/apiKeys/${encodeURIComponent(
        apiKeyValues.name,
      )}?organisationId=${organisationId}`,
      "PUT",
      apiKeyValues,
    );
    const updatedApiKey = await response.json();

    if (response.status !== 200) {
      throw new Error(updatedApiKey.messages.join(". "));
    }

    return updatedApiKey;
  } catch (err) {
    throw new Error(
      `Unable to edit api key for user as admin for organisation '${organisationId}' and api key '${apiKeyValues.displayName}' : ${err}`,
    );
  }
};

const deleteAdminUserApiKey = async (
  apiKeyName: string,
  organisationId: string,
) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/apiKeys/${encodeURIComponent(
        apiKeyName,
      )}?organisationId=${organisationId}`,
      "DELETE",
    );
    const deletedApiKey = await response.json();

    if (response.status !== 200) {
      throw new Error(deletedApiKey.messages.join(". "));
    }

    return deletedApiKey;
  } catch (err) {
    throw new Error(
      `Unable to delete api key for user as admin for organisation '${organisationId}' and api key '${apiKeyName}' : ${err}`,
    );
  }
};

const cancelAdminUserFutureSubscription = async (organisationId: string) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/subscriptions/nextSubscription/${organisationId}`,
      "DELETE",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to cancel subscription for user as admin for organisation '${organisationId}' : ${err}`,
    );
  }
};

const customDatasetsCreate = async (
  customDatasets: CustomDatasetRowType[],
  apigeeDeveloperId: string,
  identityState: User,
) => {
  const postData = customDatasets.map(
    (customDataset: CustomDatasetRowType): CustomDatasetsPost => ({
      ...customDataset,
      apigeeDeveloperId: apigeeDeveloperId,
      creator: identityState.firstName + " " + identityState.lastName,
    }),
  );

  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/custom/`,
      "POST",
      postData,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to create '${customDatasets.length}' custom datasets for account '${apigeeDeveloperId}' : ${err}`,
    );
  }
};

interface CustomDatasetsManageBody {
  status: string;
  ids: string[];
}

const manageCustomDatasetsStatus = async (body: CustomDatasetsManageBody) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/custom/manage`,
      "POST",
      body,
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(
      `Unable to change the status for '${body.ids.length}' custom datasets to '${body.status}' : ${err}`,
    );
  }
};

const deleteCustomDataset = async (customDatasetId: string) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/custom/${customDatasetId}`,
      "DELETE",
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(
      `Unable to delete the custom dataset '${customDatasetId}' : ${err}`,
    );
  }
};

const createBatchConfig = async (batchConfig: BatchConfig) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/config`,
      "POST",
      batchConfig,
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(
      `Unable to save batch config '${batchConfig.batchInfo.batchId}' : ${err}`,
    );
  }
};

const quoteBatchJob = async (batchId: string) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/${batchId}/quote`,
      "POST",
      {},
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to quote batch job '${batchId}' : ${err}`);
  }
};

const startBatchJob = async (
  batchId: string,
  name: string,
  totalRows: number,
  estimatedCost: number,
) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/${batchId}/start`,
      "POST",
      {
        name,
        totalRows,
        estimatedCost,
      },
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to start batch job '${batchId}' : ${err}`);
  }
};

const stopBatchJob = async (batchId: string) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/${batchId}/stop`,
      "POST",
      {},
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to stop batch job '${batchId}' : ${err}`);
  }
};

const createClipConfig = async (clipConfig: ClipConfiguration) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/config`,
      "POST",
      clipConfig,
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to save clip '${clipConfig.id}' : ${err}`);
  }
};

const quoteClip = async (clipId: string) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/${clipId}/quote`,
      "POST",
      {},
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to quote clip '${clipId}' : ${err}`);
  }
};

const extractClip = async (
  clipId: string,
  paymentMethod: "credits" | "invoice",
) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/${clipId}/extract`,
      "POST",
      {
        paymentMethod: paymentMethod,
      },
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to submit clip '${clipId}' : ${err}`);
  }
};

const invoiceClip = async (clipId: string) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/${clipId}/invoice`,
      "POST",
      {},
    );
    const statusResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(statusResponse.messages.join(". "));
    }

    return statusResponse;
  } catch (err) {
    throw new Error(`Unable to invoice clip '${clipId}' : ${err}`);
  }
};

const validateCoupon = async (promoCode: string): Promise<Coupon> => {
  const response = await makeApiRequestPostToUrl(
    `${import.meta.env.VITE_DELIVERY_API_URL}`,
    `/coupon/${promoCode}`,
    "POST",
    {},
  );
  const couponResponse = await response.json();

  if (response.status === 400) {
    throw new Error(couponResponse.messages);
  }

  if (response.status !== 200) {
    throw new Error("An error occured applying the coupon");
  }

  return couponResponse;
};

const submitClipGeometryFile = async (
  s3Key: string,
): Promise<GeoJSONFeatureCollection> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/processFileGeometry`,
      "POST",
      {
        s3Key: s3Key,
      },
    );
    const geometryResponse = await response.json();

    if (response.status !== 200) {
      throw new Error(geometryResponse.messages.join(". "));
    }
    return geometryResponse;
  } catch (err) {
    throw new Error(`Unable to submit clip geometry file: ${err}`);
  }
};

const adminCreateVaultItem = async (
  adminVaultItem: AdminOrganisationVault,
): Promise<AdminOrganisationVault> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/vault/${adminVaultItem.accountId}`,
      "POST",
      adminVaultItem,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }
    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to create a note for the developer '${adminVaultItem.accountId}' : ${err}`,
    );
  }
};

const adminConfirmAccount = async (accountId: string): Promise<any> => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/accounts/${accountId}/confirm`,
      "PUT",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to confirm account as admin for account:'${accountId}' : ${err}`,
    );
  }
};

const adminDemosGrantAccess = async (demoId: string, orgId: string) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/demos/${demoId}/${orgId}`,
      "PUT",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to grant ${demoId} demo access to orgId ${orgId}: ${err}`,
    );
  }
};

const adminDemosRevokeAccess = async (demoId: string, orgId: string) => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/demos/${demoId}/${orgId}`,
      "DELETE",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to revoke ${demoId} demo access to orgId ${orgId}: ${err}`,
    );
  }
};

const adminEditDemoStatus = async (demoId: string, status: DemoStatus) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/demos/${demoId}`,
      "PUT",
      {
        status: status,
      },
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to edit ${demoId} status to ${status}: ${err}`);
  }
};

/**
 * Migrates multiple members from one organisation to another
 * @param memberUserIds List of member user IDs to migrate
 * @param appsNames List of apps Names to migrate
 * @param currentOrgId Source organisation ID
 * @param newOrgId Target organisation ID
 * @param identityState User authentication state
 * @returns Promise resolving to an object mapping member IDs to migration success status
 * @throws Error if migration fails
 */
export const migrateMembers = async (
  memberUserIds: string[],
  appsNames: string[],
  migrateClipsIds: string[],
  migrateCustomDatasetsIds: string[],
  migrateBatchJobsIds: string[],
  currentOrgId: string,
  newOrgId: string,
): Promise<MigrateMembersResponse> => {
  const payload: MigrateMembersRequest = {
    member_user_ids: memberUserIds,
    apps_names: appsNames,
    clips_ids: migrateClipsIds,
    custom_datasets_ids: migrateCustomDatasetsIds,
    batch_jobs_ids: migrateBatchJobsIds,
    current_organisation_id: currentOrgId,
    new_organisation_id: newOrgId,
  };

  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/admin/migrate/members",
      "POST",
      payload,
    );

    const result: MigrateMembersResponse = await response.json();

    if (!response.ok) {
      throw new Error(
        `Migration failed with status ${response.status}: ${
          (result as any).error || "Unknown error"
        }`,
      );
    }

    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to migrate members: ${errorMessage}`);
  }
};

const createAdminConnector = async (
  organisationId: string,
  values: Connector,
): Promise<Connector> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/connectors/${organisationId}`,
      "POST",
      values,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`${err}`);
  }
};

const editAdminConnector = async (
  organisationId: string,
  values: Connector,
): Promise<Connector> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/connectors/${organisationId}`,
      "PUT",
      values,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`${err}`);
  }
};

const deleteAdminConnector = async (
  organisationId: string,
  connectorId: string,
): Promise<{ connectorId: string }> => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/connectors/${organisationId}/${connectorId}`,
      "DELETE",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to delete a connector as admin for organisation '${organisationId}' : ${err}`,
    );
  }
};

const createAdminPipeline = async (
  organisationId: string,
  values: Pipeline,
): Promise<Pipeline> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/pipelines/${organisationId}`,
      "POST",
      values,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`${err}`);
  }
};

const editAdminPipeline = async (
  organisationId: string,
  values: Pipeline,
): Promise<Pipeline> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/pipelines/${organisationId}`,
      "PUT",
      values,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`${err}`);
  }
};

const deleteAdminPipeline = async (
  organisationId: string,
  pipelineId: string,
): Promise<{ pipelineId: string }> => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/pipelines/${pipelineId}/organisation/${organisationId}`,
      "DELETE",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to delete a pipeline as admin for organisation '${organisationId}' : ${err}`,
    );
  }
};

const adminTriggerPipeline = async (
  pipelineId: string,
): Promise<ManualTriggerResponse> => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/pipelines/${pipelineId}/trigger`,
      "POST",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to trigger pipeline '${pipelineId}' : ${err}`);
  }
};

const submitBuildingsSample = async (
  requestData: BuildingsSampleRequest,
): Promise<string> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/buildingsSample",
      "POST",
      requestData,
    );

    if (response.status !== 200) {
      const errorData = await response.json();
      throw new Error(
        errorData.messages?.join(". ") ||
          `Request failed with status ${response.status}`,
      );
    }

    const presignedUrl = await response.text();

    // Clean up the URL - remove any surrounding quotes or whitespace
    const cleanedUrl = presignedUrl.replace(/^["']|["']$/g, "").trim();

    return cleanedUrl;
  } catch (err) {
    throw new Error(`Unable to request buildings sample: ${err}`);
  }
};

const updateCognitoRole = async (
  cognitoUser: any,
  role: string,
): Promise<string> => {
  try {
    await Auth.updateUserAttributes(cognitoUser, {
      "custom:role": role,
    });
    return role;
  } catch (err) {
    throw new Error(`Unable to update custom:role: ${err}`);
  }
};

const adminChangePipelineStatus = async (
  pipelineId: string,
  body: PipelineStatusBody,
): Promise<PipelineStatusBody> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/pipelines/${pipelineId}/status`,
      "POST",
      body,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to change the pipeline status '${pipelineId}' : ${err}`,
    );
  }
};

const adminCustomDatasetEdit = async (
  customDatasetId: string,
  customDatasetEdit: CustomDatasetEdit,
): Promise<CustomDataset> => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/custom/edit/${customDatasetId}`,
      "PUT",
      customDatasetEdit,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to edit custom dataset: '${customDatasetId}' : ${err}`,
    );
  }
};

const testAdminConnector = async (
  organisationId: string,
  connectorId: string,
  readOnly: boolean = true,
): Promise<{ success: boolean; message: string; details?: any }> => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/connectors/${organisationId}/${connectorId}/test?read_only=${readOnly}`,
      "POST",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`${err}`);
  }
};

const resendSignUp = async (email: string): Promise<any> => {
  try {
    return await Auth.resendSignUp(email);
  } catch (err) {
    throw new Error(
      `Unable to resend verification for email '${email}': ${err}`,
    );
  }
};

const confirmSignUp = async (username: string, code: string): Promise<any> => {
  try {
    return await Auth.confirmSignUp(username, code);
  } catch (err) {
    throw new Error(
      `Unable to confirm sign up for username '${username}': ${err}`,
    );
  }
};

interface SignInParams {
  email: string;
  password: string;
  termsOfUse?: boolean;
}

const signIn = async (params: SignInParams): Promise<any> => {
  try {
    const cognitoUser = await Auth.signIn(
      params.email.toLowerCase(),
      params.password,
      {
        "custom:dev_t_and_c": String(params.termsOfUse),
      },
    );
    return cognitoUser;
  } catch (err) {
    throw new Error(`Unable to sign in for email '${params.email}': ${err}`);
  }
};

export {
  addPaymentMethod,
  adminAddOrganisationMember,
  adminAddPaymentMethod,
  adminChangePipelineStatus,
  adminConfirmAccount,
  adminCreateVaultItem,
  adminCustomDatasetEdit,
  adminDeleteAccount,
  adminDeleteOrganisationMember,
  adminDemosGrantAccess,
  adminDemosRevokeAccess,
  adminDisableAccount,
  adminEditDemoStatus,
  adminEnableAccount,
  adminSetOrganisationStatus,
  adminTriggerPipeline,
  adminUndoScheduledCancellation,
  adminUpdateCredits,
  cancelAdminUserFutureSubscription,
  cancelAdminUserSubscription,
  cancelFutureSubscription,
  changeDatasetStatus,
  changeReleaseStatus,
  confirmSignUp,
  createAdminConnector,
  createAdminOrganisation,
  createAdminPipeline,
  createAdminUserApiKey,
  createApiKey,
  createBatchConfig,
  createClipConfig,
  customDatasetsCreate,
  deleteAdminConnector,
  deleteAdminPipeline,
  deleteAdminUser,
  deleteAdminUserApiKey,
  deleteAdminUserMembership,
  deleteApiKey,
  deleteBatchJobs,
  deleteClip,
  deleteCustomDataset,
  deleteOrganisationInvitation,
  deleteOrganisationMember,
  deleteRelease,
  editAdminAccount,
  editAdminConnector,
  editAdminOrganisation,
  editAdminOrganisationInfo,
  editAdminPipeline,
  editAdminUserApiKey,
  editAdminUserSubscription,
  editApiKey,
  editOrganisation,
  extractClip,
  invoiceClip,
  manageCustomDatasetsStatus,
  putRelease,
  quoteBatchJob,
  quoteClip,
  resendSignUp,
  scheduleCancellation,
  signIn,
  startBatchJob,
  stopBatchJob,
  submitBuildingsSample,
  submitClipGeometryFile,
  submitNewOrganisation,
  submitOrganisationInvitations,
  testAdminConnector,
  undoScheduledCancellation,
  updateAdminUserRoleOrgMembership,
  updateCognitoRole,
  updateOverageLimit,
  updateSubscription,
  updateUserRoleOrgMembership,
  userAcceptInvitation,
  userRejectInvitation,
  validateCoupon,
};

export type { ReactQueryMutationError };
